// src/modules/Projects/Projects.js
import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import { secureGet, securePost, secureDelete } from "../../config/api";
import { formatDateTime } from "../../utils/format";

import Filter from "../../components/Filter";
import Rows from "../../components/Rows";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import TableLoadingArea from "../../components/TableLoadingArea";
import SnackbarNotification from "../../components/SnackbarNotification";
import { PrimaryButton } from "../../components/PrimaryButton";
import  UserContext  from "../../Shared/UserContext/UserContext";
import DeleteIcon from "../../icons/trashR.svg";
import AddIcon from "../../icons/plusB.svg";

const STATUS_COLORS = { 
  CREATED: "#A8ADC3",
  ONGOING: "#FEA26F",
  FINISHED: "#06D6A0",
  DELIVERED: "#3872B0",
};

const Projects = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const topRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [currentPageData, setCurrentPageData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const { state: { role } } = useContext(UserContext);

  const [filters, setFilters] = useState({
    needle: "",
    clientId: null,
    status: null,
    dateRange: {},
  });

  const [clients, setClients] = useState([]);
  const statusOptions = [
    { value: "CREATED", label: "Créé" },
    { value: "ONGOING", label: "En cours" },
    { value: "FINISHED", label: "Terminé" },
    { value: "DELIVERED", label: "Livré" },
  ];

  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const columns = [
    { label: "ID", key: "projectNumber" },
    {
      label: t(`columns.createdAt`),
      key: "createdAt",
      render: (date) => (date ? formatDateTime(date) : "—"),
    },
    {
      label: "Client",
      key: "clientName",
    },
    {
      label: "Marque",
      key: "vehicle",
      render: (vehicle) => (vehicle?.brand?.name ? vehicle?.brand?.name : "—"),
    },
    {
      label: "Modèle",
      key: "vehicle",
      render: (vehicle) => (vehicle?.model ? vehicle?.model : "—"),
    },
    {
      label: "Couleur",
      key: "vehicle",
      render: (vehicle) => (vehicle?.color ? vehicle?.color : "—"),
    },
    {
      label: t(`columns.tasksCount`),
      key: "tasks",
      align: "right",
      render: (tasks) => tasks?.length ?? 0,
    },
    {
      label: t(`filters.payments.status`),
      key: "status",
      render: (status) => {
        const color = STATUS_COLORS[status] || "#A8ADC3";
        return (
          <span
            className="text-white text-[12px] font-semibold px-2 py-1 rounded-full inline-block w-fit text-center"
            style={{ backgroundColor: color }}
          >
            {t(`project_status.${status}`).toUpperCase()}
          </span>
        );
      },
    },
/*     ...(role === "ROLE_WORKSHOP_MANAGER" || role === "ROLE_ADMIN" || role === "ROLE_SERVICE_CONSULTANT"
      ? [
          {
            key: "actions",
            render: (_, item) => (  
              <div className="flex items-center justify-end gap-x-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item);
                  }}
                  className="w-8 h-8 flex items-center justify-center bg-[#FFFFFF] rounded-[12px] shadow-[0px_6px_26px_#626B7D12]"
                  title="Supprimer"
                >
                  <img src={DeleteIcon} alt="delete" className="w-5 h-5" />
                </button>
              </div>
            ),
          },
        ]
      : []
    ), */
  ];

  const filterOptions = [
    { name: "needle", type: "search" },
    { name: "dateRange", label: "Date", type: "daterange" },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: clients.map((c) => ({ value: c.id, reference: c.code, name: c.name })),
    },
    {
      name: "status",
      label: t(`filters.payments.status`),
      type: "dropdown",
      options: statusOptions,
    }
  ];

  // dropdowns
  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchDropdowns = async () => {
      try {
        const clientsRes = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientsRes.data || []);
      } catch (e) {
          if (e.name !== "AbortError") console.error(e);
        }
      };

      fetchDropdowns();
      return () => abortController.abort();
    }, [filters?.clientId]);

  // fetch page
  const fetchPaged = useCallback(
    async (page = 0, size = pageSize, active = filters, signal) => {
      console.log("Fetching projects with filters:", active);
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("size", size);
      if (active.needle) params.append("needle", active.needle);
      if (active.clientId) params.append("clientId", active.clientId);
      if (active.dateRange.startDate) params.append("startDate", active.dateRange.startDate);
      if (active.dateRange.endDate) params.append("endDate", active.dateRange.endDate);
      if (active.status) params.append("status", active.status);

      try {
        const res = await secureGet(`/projects?${params.toString()}`, showSnackbar, {
          signal,
        });
        const { content = [], totalPages = 1, totalElements = 0 } = res?.data || {};
        setCurrentPageData(content);
        console.log("Projects fetched:", content);
        setTotalPages(totalPages);
        setTotalElements(totalElements);
      } catch (e) {
        if (e.name !== "AbortError") console.error("Error fetching projects:", e);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, filters]
  );

  // initial load / restore state from navigation
  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);
      fetchPaged(restoredPage, restoredSize, restoredFilters, signal);
    } else {
      fetchPaged(0, pageSize, filters, signal);
    }

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaged(page, pageSize, filters);
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaged(0, size, filters);
  };

  // filters
  const handleFilterChange = (name, value) => {
    const updated = { ...filters };

    if (name === "dateRange") {
      updated.dateRange = {
        startDate: value?.startDate || null,
        endDate: value?.endDate || null,
      };
    } else {
      updated[name] = value;
    }

    setFilters(updated);
    setCurrentPage(0);
    fetchPaged(0, pageSize, updated);
  };

  const resetFilters = () => {
    const def = { needle: "", clientId: null, status: null, dateRange: {} };
    setFilters(def);
    setCurrentPage(0);
    fetchPaged(0, pageSize, def);
  };

  // actions
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await secureDelete(`/projects/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaged(currentPage, pageSize, filters);
      showSnackbar(t("confirmation.deleted"), "success");
    } catch (e) {
      console.error("Delete error:", e);
      setDeletePopupVisible(false);
    }
  };

  const handleRowClick = (item) => {
    history.push({
      pathname: `/projects/details/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox — {t("titles.projects")}</title>
      </Helmet>

      <div className="flex items-center mb-[15px]">
        <h1 className="text-[40px] font-semibold">{t("titles.projects")}</h1>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <TableLoadingArea loading={loading} minHeight={200}>
        <Rows columns={columns} data={currentPageData} onRowClick={handleRowClick} emptyLabel={t('empty.projects')} />
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Projects;
